/**
 * Page Performance — state for one server-paged, server-sorted, searchable
 * table: search (debounced before it reaches the server), sort, page and
 * rows-per-page (the person's preference via `usePageSize`).
 *
 * The page number snaps back to 1 whenever the scope, search, filter or
 * sort changes — tracked by a key instead of an effect, so nothing renders
 * twice and no stale "page 4 of 1" is ever requested.
 */
import { useEffect, useState } from 'react'
import { usePageSize } from '../../lib/display-prefs'
import { MAX_LIMIT } from '../../lib/page-perf/api-client'

export function usePerfTable({ defaultSort, sizes = [10, 25, 50, 100], resetKey = '' }) {
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [sort, setSort] = useState(defaultSort)
  const [limit, setLimitRaw, limitOptions] = usePageSize({ options: sizes, max: MAX_LIMIT })
  const [paging, setPaging] = useState({ page: 1, key: '' })

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300)
    return () => clearTimeout(t)
  }, [q])

  const first = sort?.[0]
  const key = `${resetKey}|${debouncedQ}|${first?.key ?? ''}|${first?.dir ?? ''}|${limit}`
  const page = paging.key === key ? paging.page : 1

  return {
    q,
    setQ,
    sort,
    setSort,
    page,
    setPage: (p) => setPaging({ page: p, key }),
    limit,
    setLimit: (n) => setLimitRaw(n),
    limitOptions,
    params: {
      q: debouncedQ,
      sort: first?.key ?? null,
      dir: first?.dir ?? null,
      page,
      limit,
    },
  }
}
